/*
  MCF 결제 진입 확인 팝업 — 체크아웃 버튼과 연결
  설계·함정 상세: local/mcf/2026-08-07-checkout-popup-v3.md

  🔴 애매하면 전부 "통과" 쪽으로 떨어뜨린다 — 배송 안내를 못 보는 것보다 결제를
     못 하는 쪽이 훨씬 나쁘다. 팝업이 없으면 아무것도 하지 않는 것도 설계다.
  🔴 로드 순서가 중요하다. cart-partial-checkout.js 보다 "앞에" 있어야 한다
     (같은 버튼을 캡처 단계에서 잡고, 리스너는 등록 순서대로 돈다).
*/
(() => {
  if (window.__mcfCheckoutPopup) return; /* 드로어 + 카트 페이지 이중 로드 가드 */
  window.__mcfCheckoutPopup = true;

  /* Continue 로 다시 누를 때 이 핸들러만 비켜준다. 나머지 리스너는 평소대로 돈다. */
  let passThrough = false;
  let checkoutRefreshPending = false;
  const log = (event, detail = {}) => console.info(`[MCF checkout] ${event}`, detail);

  const popupFor = (cartRoot) =>
    cartRoot ? cartRoot.querySelector('.mcf-ship-modal') : document.querySelector('.mcf-ship-modal');

  /* 🔴 드로어의 .drawer__inner 가 transform 을 가져서, 그 안의 position:fixed 는
     화면이 아니라 드로어 패널 기준이 된다. 열 때 body 로 옮겨 화면 기준을 되찾는다.
     같은 방식: assets/custom-size-guide.js (cc6797de) */
  const PLACEHOLDER = Symbol('mcfPopupPlaceholder');

  const portalToBody = (popup) => {
    if (popup.parentElement === document.body) return;
    const placeholder = document.createComment('mcf-checkout-popup');
    popup.parentElement.insertBefore(placeholder, popup);
    popup[PLACEHOLDER] = placeholder;
    document.body.appendChild(popup);
  };

  /* 열린 사이 카트가 다시 그려졌으면(자리표시자가 문서에서 떨어졌으면) 이 노드는
     낡은 티어 맵을 든 잔재다 — 되돌리지 않고 버린다 */
  const restoreFromBody = (popup) => {
    const placeholder = popup[PLACEHOLDER];
    popup[PLACEHOLDER] = null;
    if (!placeholder) return;

    if (placeholder.parentNode) {
      placeholder.parentNode.insertBefore(popup, placeholder);
      placeholder.remove();
    } else {
      popup.remove();
    }
  };

  /* 닫기 버튼·오버레이·ESC 가 모두 open 속성으로 모이므로 속성 하나만 지켜본다.
     여는 쪽은 아래에서 직접 옮긴다 — 한 프레임도 어긋나 보이지 않게. */
  const watchOpenState = (popup) => {
    if (popup.__mcfWatched) return;
    popup.__mcfWatched = true;
    new MutationObserver(() => {
      if (popup.hasAttribute('open')) portalToBody(popup);
      else restoreFromBody(popup);
    }).observe(popup, { attributes: true, attributeFilter: ['open'] });
  };

  /* 카트가 다시 그려지면 팝업도 새 노드가 된다 — 그때마다 다시 걸어준다 */
  const watchAll = () => document.querySelectorAll('.mcf-ship-modal').forEach(watchOpenState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAll, { once: true });
  } else {
    watchAll();
  }
  document.addEventListener('cart:updated', watchAll);

  /* 선택된 라인의 티어를 모아 주문 전체의 티어와 Standard 배송 불가 대상을 정한다.
     뱃지와 무관하게 KR 재고가 선택 수량을 충족해야 Standard 주문에 남길 수 있다. */
  const selectionOf = (popup, cartRoot) => {
    let tiers;
    let standardEligible;
    let lineDetails;
    try {
      tiers = JSON.parse(popup.dataset.mcfLineTiers || '{}');
      standardEligible = JSON.parse(popup.dataset.mcfLineStandardEligible || '{}');
      lineDetails = JSON.parse(popup.dataset.mcfLineDetails || '{}');
    } catch (e) {
      console.warn('[MCF checkout] popup data parse failed', {
        error: e,
        tiers: popup.dataset.mcfLineTiers,
        standardEligible: popup.dataset.mcfLineStandardEligible,
        lineDetails: popup.dataset.mcfLineDetails,
      });
      return null; /* 맵을 못 읽으면 판정하지 않는다 → 호출부가 통과시킨다 */
    }

    /* 🔴 [중요] 카트 페이지와 드로어가 동시에 존재하므로 전역 조회하면 선택 상태가 섞인다.
       회귀 case: docs/superpowers/specs/2026-08-11-mcf-mixed-cart-checkout-design.md */
    const selectionRoot = cartRoot?.querySelectorAll ? cartRoot : document;
    const boxes = [...selectionRoot.querySelectorAll('[data-cart-line-select]')];
    /* 체크박스가 없는 화면(선택 결제 미노출)에서는 카트 전체가 대상이다 */
    const selectedKeys = boxes.length ? boxes.filter((b) => b.checked).map((b) => b.dataset.key) : Object.keys(tiers);
    const keys = selectedKeys.filter((key) => Object.prototype.hasOwnProperty.call(tiers, key));
    if (!keys.length) return null; /* 선택 0개 — 기존 흐름이 자기 문구로 안내한다 */

    /* 체크 해제한 라인은 결제 대상이 아니므로 배송지·재고 합계에서 모두 제외한다. */
    const selectedQuantityByVariant = new Map();
    for (const key of keys) {
      const detail = lineDetails[key];
      const variantId = detail?.variantId;
      const quantity = Number(detail?.quantity);
      if (variantId == null || !Number.isFinite(quantity)) continue;
      selectedQuantityByVariant.set(variantId, (selectedQuantityByVariant.get(variantId) || 0) + quantity);
    }

    const selectedTiers = Object.fromEntries(keys.map((key) => {
      const detail = lineDetails[key];
      const selectedQuantity = selectedQuantityByVariant.get(detail?.variantId);
      const mcfStock = Number(detail?.mcfStock);
      if (Number.isFinite(selectedQuantity) && Number.isFinite(mcfStock)) {
        return [key, mcfStock >= selectedQuantity ? 'express' : 'standard'];
      }
      return [key, tiers[key] === 'express' ? 'express' : 'standard'];
    }));
    const hasExpress = keys.some((key) => selectedTiers[key] === 'express');
    const hasStandard = keys.some((key) => selectedTiers[key] === 'standard');
    const mixed = hasExpress && hasStandard;
    const tier = hasStandard ? 'standard' : 'express';

    /* 최종 배송지의 재고를 선택된 동일 variant 수량 합계와 비교한다. */
    const stockField = tier === 'standard' ? 'krStock' : 'mcfStock';
    const removalKeys = keys.filter((key) => {
      const detail = lineDetails[key];
      const selectedQuantity = selectedQuantityByVariant.get(detail?.variantId);
      const stock = Number(detail?.[stockField]);
      if (Number.isFinite(selectedQuantity) && Number.isFinite(stock)) return stock < selectedQuantity;

      /* 구 버전 섹션 마크업이 캐시된 순간만 Standard 서버 판정으로 안전하게 폴백한다. */
      return tier === 'standard' && standardEligible[key] === false;
    });

    /* Standard 원인 라인 — 시안대로 줄째 뺀다 (수량 초과 라인도 통째로, 2026-10-06 결정) */
    const standardItems = tier === 'standard' ? keys.filter((key) => selectedTiers[key] === 'standard') : [];
    /* 다 빼면 결제할 게 없다 — 그때는 Remove all 을 숨긴다 */
    const canSwitchToExpress = hasExpress;

    console.info('[MCF checkout] selection', {
      selectedKeys: keys,
      serverTiers: tiers,
      selectedTiers,
      standardEligible,
      lineDetails,
      tier,
      stockField,
      selectedQuantityByVariant: Object.fromEntries(selectedQuantityByVariant),
      hasExpress,
      hasStandard,
      mixed,
      removalKeys,
      removalItems: removalKeys.map((key) => ({ key, ...lineDetails[key] })),
      standardItems,
      canSwitchToExpress,
    });

    return {
      tier,
      mixed,
      removalKeys,
      removalItems: removalKeys.map((key) => ({ key, ...lineDetails[key] })),
      standardItems,
      canSwitchToExpress,
    };
  };

  const showState = (popup, selection) => {
    popup.querySelectorAll('[data-mcf-state]').forEach((state) => {
      state.hidden = state.dataset.mcfState !== selection.tier;
    });
    popup.querySelectorAll('[data-mcf-removal-item]').forEach((item) => {
      item.hidden = !selection.removalKeys.includes(item.dataset.key);
    });
    const confirmTitle = popup.querySelector('[data-mcf-confirm] [data-mcf-count-text]');
    if (confirmTitle) fillCount(confirmTitle, selection.removalKeys.length);
    showConfirm(popup, false);
    const removalError = popup.querySelector('[data-mcf-removal-error]');
    if (removalError) removalError.hidden = true;
    renderStandardItems(popup, selection);
    log('popup state', selection);
  };

  /* 제거 확인은 같은 팝업 안에서 화면만 바꾼다 — 포털·열림 상태를 한 벌로 유지 */
  const showConfirm = (popup, on) => {
    popup.classList.toggle('is-confirming', on);
    const titleId = popup.id + (on ? '-confirm-title' : '-title');
    popup.setAttribute('aria-labelledby', titleId);
    /* 버튼에 포커스를 주면 파란 테두리가 떠서 제목으로 옮긴다 — 낭독은 제목부터 */
    if (on) popup.querySelector('[data-mcf-confirm] .mcf-ship-modal__title')?.focus();
  };

  const fillCount = (el, n) => {
    el.textContent = (n === 1 ? el.dataset.one : el.dataset.other).replace('{n}', n);
  };

  const renderStandardItems = (popup, selection) => {
    const box = popup.querySelector('[data-mcf-standard-items]');
    if (!box) return;
    const keys = selection.standardItems;
    box.hidden = !keys.length;
    box.querySelectorAll('[data-mcf-count-text]').forEach((el) => fillCount(el, keys.length));
    box.querySelector('[data-mcf-remove-standard]').hidden = !selection.canSwitchToExpress;
    box.querySelectorAll('[data-mcf-standard-item]').forEach((row) => {
      row.hidden = !keys.includes(row.dataset.key);
    });
  };

  const passCheckoutThrough = (button) => {
    passThrough = true;
    button.click();
    passThrough = false;
  };

  /* ── 제거 상품 복원 (KAN-23 마) ──────────────────────────────────────
     카트 속성은 주문에 실려 가고 결제 뒤 카트는 새로 생기므로 localStorage 에 둔다.
     카트 토큰이 바뀌었으면 결제가 끝난 것으로 보고 다시 담는다. */
  const RESTORE_KEY = 'mcf:removed-items';
  const RESTORE_TTL = 7 * 24 * 60 * 60 * 1000;
  const cartToken = (cart) => String(cart?.token || '').split('?')[0];

  const readRestore = () => {
    try {
      return JSON.parse(localStorage.getItem(RESTORE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  };

  const rememberRemoved = (before, after, updates) => {
    if (!Array.isArray(before?.items) || !cartToken(after)) return;
    const items = [];
    Object.entries(updates).forEach(([key, keep]) => {
      const line = before.items.find((item) => item.key === key);
      if (!line || line.properties?._Gifted) return; /* 기프트는 앱이 다시 넣는다 */
      const quantity = line.quantity - keep;
      if (quantity > 0) items.push({ id: line.variant_id, quantity, properties: line.properties || {} });
    });
    if (!items.length) return;
    /* 같은 카트에서 여러 번 지웠으면 이어 붙인다 */
    const saved = readRestore();
    const token = cartToken(after);
    const prev = saved?.token === token ? saved.items : [];
    try {
      localStorage.setItem(RESTORE_KEY, JSON.stringify({ token, savedAt: Date.now(), items: [...prev, ...items] }));
      log('removed items saved for restore', { token, items });
    } catch (e) {
      console.warn('[MCF checkout] restore save failed', e);
    }
  };

  const restoreRemoved = async () => {
    const saved = readRestore();
    if (!saved?.items?.length) return;
    if (Date.now() - saved.savedAt > RESTORE_TTL) {
      localStorage.removeItem(RESTORE_KEY);
      return;
    }
    const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
    if (!cart || cartToken(cart) === saved.token) return; /* 아직 결제 전 */

    /* 먼저 지워 새로고침·다른 탭에서 두 번 담기지 않게 한다 */
    localStorage.removeItem(RESTORE_KEY);
    log('restoring removed items', { from: saved.token, to: cartToken(cart), items: saved.items });
    /* 하나씩 담는다 — 한 상품이 품절이어도 나머지는 들어간다. addItem 은 직렬 큐를 탄다 */
    for (const item of saved.items) {
      await window.FoxThemeCartStore?.addItem(item).catch((error) => {
        console.warn('[MCF checkout] restore failed', { item, error });
      });
    }
    /* addItem 은 JSON 만 발행한다 — 카트 페이지 줄까지 다시 그리려면 섹션을 받는다 */
    await window.FoxThemeCartStore?.refresh?.().catch(() => {});
  };

  /* 여러 라인을 한 요청으로 바꾼다. removeItem()을 반복하면 각 응답이 카트 섹션을
     다시 그려 결제 버튼 참조가 끊기므로 JSON cart만 갱신한다.
     🔴 cart.js 의 직렬 큐를 탄다 — 다른 변경과 겹치면 422·되살아남이 난다. */
  const updateLines = (updates) => {
    const request = async () => {
      /* 복원용으로 지우기 직전 라인(속성 포함)을 잡아둔다 */
      const before = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
      const url = window.FoxTheme?.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      const cart = await response.json().catch(() => null);
      if (!response.ok) throw new Error(`MCF cart update failed (${response.status})`);
      rememberRemoved(before, cart, updates);
      return cart;
    };
    /* 다 지운 유료 라인만 되살아남 가드에 올린다 — cart.js optimisticRemove 와 같은 규칙 */
    const purged = window.FoxThemeCartState?.purgedKeys;
    const guarded = Object.entries(updates)
      .filter(([key, quantity]) => quantity === 0)
      .map(([key]) => key)
      .filter((key) => !document.querySelector(`[data-key="${CSS.escape(key)}"][data-eg-gift="true"]`));
    guarded.forEach((key) => purged?.add(key));
    const run = typeof enqueueCartMutation === 'function' ? enqueueCartMutation(request) : request();
    /* 실패하면 라인이 그대로 있으니 가드를 풀어야 카트가 다시 그려진다 */
    return run.catch((error) => {
      guarded.forEach((key) => purged?.delete(key));
      throw error;
    });
  };

  const removeLines = (keys) => updateLines(Object.fromEntries(keys.map((key) => [key, 0])));

  /* 요청한 수량대로 반영됐는지 응답으로 확인한다 */
  const assertSettled = (cart, updates) => {
    if (!Array.isArray(cart?.items)) throw new Error('MCF cart update could not be verified');
    const quantities = new Map();
    cart.items.forEach((item) => quantities.set(item.key, (quantities.get(item.key) || 0) + item.quantity));
    const unsettled = Object.entries(updates).filter(([key, quantity]) => (quantities.get(key) || 0) !== quantity);
    if (unsettled.length) throw new Error(`MCF cart update did not settle: ${JSON.stringify(unsettled)}`);
  };

  const openPopup = (popup, button) => {
    const cartRoot = button.closest('cart-drawer, main-cart');
    const selection = selectionOf(popup, cartRoot);
    if (!selection) return false;

    showState(popup, selection);
    popup.__mcfTarget = button;
    popup.__mcfSelection = selection;
    watchOpenState(popup);
    portalToBody(popup);
    popup.setAttribute('open', '');

    const continueBtn = popup.querySelector('[data-mcf-continue]');
    if (!continueBtn) return true;

    /* 매번 새로 달지 않도록 한 번만 묶는다 — 팝업은 재렌더 전까지 같은 노드다 */
    if (!continueBtn.dataset.bound) {
      continueBtn.dataset.bound = '1';
      continueBtn.addEventListener('click', async () => {
        const target = continueBtn.__mcfTarget;
        if (!target) return;

        const removalKeys = continueBtn.__mcfRemovalKeys || [];
        /* 지울 상품이 있으면 먼저 확인받는다 — Remove and Continue 가 다시 여기로 온다 */
        if (removalKeys.length && !continueBtn.__mcfConfirmed) {
          log('removal confirm shown', { removalKeys });
          showConfirm(popup, true);
          return;
        }
        continueBtn.__mcfConfirmed = false;
        popup.removeAttribute('open'); /* 실패 문구는 버튼 아래에 뜨므로 팝업은 비켜준다 */

        if (!removalKeys.length) {
          log('continue without removal', { tier: continueBtn.__mcfSelection?.tier });
          passCheckoutThrough(target);
          return;
        }

        if (continueBtn.__mcfRemovalPending) return;
        continueBtn.__mcfRemovalPending = true;
        continueBtn.disabled = true;
        document.body.classList?.add('mcf-checkout-removing');

        const cartRoot = target.closest('cart-drawer, main-cart');
        const busy = window.FoxThemeCartBusy;
        busy?.pointFromEl(target);
        busy?.begin();

        try {
          log('removal started', {
            removalKeys,
            removalItems: continueBtn.__mcfRemovalItems || [],
          });
          log('removing lines', { keys: removalKeys });
          const cart = await removeLines(removalKeys);
          if (!Array.isArray(cart?.items)) throw new Error('MCF cart removal could not be verified');
          const remainingKeys = new Set(cart.items.map((item) => item.key));
          log('removal verification', {
            requestedKeys: removalKeys,
            remainingKeys: [...remainingKeys],
            itemCount: cart.item_count,
          });
          if (removalKeys.some((key) => remainingKeys.has(key))) {
            throw new Error('MCF Express-only item removal did not settle');
          }
          if (!cart.item_count || !cart.items.length) {
            throw new Error('MCF checkout has no remaining cart items');
          }

          if (target.isConnected !== false) {
            log('checkout resumed', { removedKeys: removalKeys });
            passCheckoutThrough(target);
          }
        } catch (error) {
          console.error('[MCF checkout] removal failed', { error, removalKeys });
          const currentPopup = popupFor(cartRoot);
          const currentButton = cartRoot && cartRoot.querySelector('[data-checkout-btn]');
          if (currentPopup && currentButton && openPopup(currentPopup, currentButton)) {
            const removalError = currentPopup.querySelector('[data-mcf-removal-error]');
            if (removalError) removalError.hidden = false;
          }
        } finally {
          document.body.classList?.remove('mcf-checkout-removing');
          busy?.end();
          continueBtn.disabled = false;
          continueBtn.__mcfRemovalPending = false;
        }
      });
    }
    const confirmBtn = popup.querySelector('[data-mcf-confirm-continue]');
    if (confirmBtn && !confirmBtn.dataset.bound) {
      confirmBtn.dataset.bound = '1';
      confirmBtn.addEventListener('click', () => {
        if (continueBtn.__mcfRemovalPending) return;
        continueBtn.__mcfConfirmed = true;
        continueBtn.click();
      });
    }
    continueBtn.__mcfTarget = button;
    continueBtn.__mcfSelection = selection;
    continueBtn.__mcfRemovalKeys = selection.removalKeys;
    continueBtn.__mcfRemovalItems = selection.removalItems;
    return true;
  };

  /* Remove all — Standard 원인 상품을 빼고 같은 팝업을 Express 로 다시 연다.
     팝업 노드는 재렌더마다 바뀌므로 document 에 한 번만 건다. */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-mcf-remove-standard]');
    if (!trigger) return;
    const popup = trigger.closest('.mcf-ship-modal');
    const selection = popup?.__mcfSelection;
    const target = popup?.__mcfTarget;
    if (!selection?.standardItems?.length || !target || popup.__mcfUpdating) return;

    popup.__mcfUpdating = true;
    trigger.disabled = true;
    const cartRoot = target.closest('cart-drawer, main-cart');
    const updates = Object.fromEntries(selection.standardItems.map((key) => [key, 0]));

    try {
      log('remove standard started', { updates, items: selection.standardItems });
      const cart = await updateLines(updates);
      assertSettled(cart, updates);
      log('remove standard settled', { itemCount: cart.item_count });

      popup.removeAttribute('open');
      /* 섹션을 다시 받아야 티어 맵이 새 카트 기준이 된다 */
      await window.FoxThemeCartStore?.refresh?.();
      const currentButton = cartRoot && cartRoot.querySelector('[data-checkout-btn]');
      const currentPopup = popupFor(cartRoot);
      if (currentButton && currentPopup) openPopup(currentPopup, currentButton);
    } catch (error) {
      console.error('[MCF checkout] remove standard failed', { error, updates });
      const removalError = popup.querySelector('[data-mcf-removal-error]');
      if (removalError) removalError.hidden = false;
    } finally {
      popup.__mcfUpdating = false;
      trigger.disabled = false;
    }
  });

  document.addEventListener(
    'click',
    async (event) => {
      if (passThrough) return;

      const button = event.target.closest('[data-checkout-btn]');
      if (!button) return;

      const cartRoot = button.closest('cart-drawer, main-cart');
      const popup = popupFor(cartRoot);
      if (!popup) return; /* 비 US · 빈 카트 — 손대지 않는다 */

      /* 🔴 stopImmediatePropagation 이어야 한다. stopPropagation 만으로는 같은
         document 에 걸린 cart-partial-checkout 의 리스너가 그대로 돈다. */
      event.preventDefault();
      event.stopImmediatePropagation();

      if (checkoutRefreshPending) return;
      checkoutRefreshPending = true;

      const busy = window.FoxThemeCartBusy;
      busy?.pointFromEl(button);
      busy?.begin();

      try {
        if (typeof window.FoxThemeCartStore?.refresh === 'function') {
          log('pre-checkout refresh started');
          const cart = await window.FoxThemeCartStore.refresh();
          log('pre-checkout refresh completed', {
            itemCount: cart?.item_count,
            keys: Array.isArray(cart?.items) ? cart.items.map((item) => item.key) : [],
          });
        }
      } catch (e) {
        console.warn('[MCF checkout] pre-checkout refresh failed; using current popup data', e);
        /* 최신화 실패는 체크아웃을 막지 않는다 — 기존 팝업 데이터로 계속한다. */
      } finally {
        busy?.end();
        checkoutRefreshPending = false;
      }

      /* 섹션 갱신으로 버튼·팝업이 교체됐으므로 반드시 새 노드를 다시 찾는다. */
      const currentButton = cartRoot && cartRoot.querySelector('[data-checkout-btn]');
      if (!currentButton) return; /* 갱신 결과 빈 카트라면 더 진행할 결제가 없다. */
      const currentPopup = popupFor(cartRoot) || popup;
      if (!openPopup(currentPopup, currentButton)) passCheckoutThrough(currentButton);
    },
    true
  );

  /* cart.js(defer)가 먼저 로드돼야 큐를 쓸 수 있다 — DOMContentLoaded 뒤에 돈다 */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreRemoved, { once: true });
  } else {
    restoreRemoved();
  }
})();
